import React, { useContext, useRef } from 'react';
import { css } from 'styled-components';
import { Page, InfiniteTableProps, message } from 'components';
import { SorterResult } from 'antd/lib/table/interface';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { useBreakpoint } from 'hooks';
import { isUserLicensed } from '@bb/users';
import { ContactDrawerContext, AuthContext, LayoutContext } from 'contexts';
import API, { ContactController } from 'api';
import {
  IGenericContact,
  ListContactType,
  ContactListSortOrder,
} from '@bb/contacts';
import { OrgDTO } from '@bb/domain/dist/types/dto/UserController/org';

import { SORT_KEY_MAP } from './constants';
import ContactsTab from './ContactsTabs';
import ContactsHeaderExtras from './PageHeaderExtras';

import {
  ContactDrawerMode,
  ContactDrawerType,
  ContactDrawerUser,
} from '../../ContactDrawer/types';

const { useState, useEffect, useCallback } = React;

const pageBodyStyle = css`
  place-self: stretch;
  flex-grow: 0;
  display: flex;
  background-color: transparent;
  .ant-tabs-content-holder {
    flex-grow: 0;
    display: flex;
    min-height: 0;
    height: 100%;
    max-height: 100%;
    .ant-tabs-content {
      display: flex;
      flex-grow: 0;
      min-height: 0;
      height: 100%;
      max-height: 100%;
    }
  }
`;

type ContactsPageParams = { tabKey: ListContactType | 'brokerage' };

const ContactsPage: React.FC = () => {
  // React Router
  const history = useHistory();
  const { tabKey } = useParams<ContactsPageParams>();
  // Search
  const debouncedSearchId = useRef<NodeJS.Timeout>();
  const isLoadingMoreSearch = useRef<boolean>(false);
  const [initialSearchLoading, setInitialSearchLoading] = useState(false);
  const [prevSearchText, setPrevSearchText] = useState(null);
  const [searchActive, setSearchActive] = useState(false);
  const [searchCounts, setSearchCounts] =
    useState<Record<ListContactType | 'brokerage', number>>(null);
  const [searchItemCount, setSearchItemCount] = useState(null);
  const [searchText, setSearchText] = useState('');
  // List
  const [activeTab, setActiveTab] = useState<ListContactType | 'brokerage'>(
    tabKey,
  );
  const [data, setData] = useState<
    (IGenericContact | OrgDTO['users'][number])[]
  >([]);
  const isLoadingMore = useRef<boolean>(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [itemCount, setItemCount] = useState(null);
  const [searchData, setSearchData] = useState<IGenericContact[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Brokerage Table
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // Filters & Sort
  const [filter, setFilter] = useState(null);
  const [sort, setSort] = useState<{
    field: string;
    order: ContactListSortOrder;
  }>(null);
  // Contact Drawer
  const { openContactDrawer, reloadToggle, triggerReload } =
    useContext(ContactDrawerContext);
  // Auth
  const { auth, hasFeature } = useContext(AuthContext);

  const { setAppTitle } = useContext(LayoutContext);

  const { md, xl } = useBreakpoint();

  useEffect(() => {
    setAppTitle('Contacts');
  }, []);

  useEffect(() => {
    if (tabKey === 'brokerage' && !hasFeature('MY_BROKERAGE'))
      history.push('/feature-missing');
    setActiveTab(tabKey);
  }, [hasFeature, tabKey]);

  useEffect(() => {
    setSort(null);
    setFilter(null);
  }, [searchActive]);

  const loadMoreItems = async (startIndex: number, stopIndex: number) => {
    if (isLoadingMore.current && startIndex) return;
    isLoadingMore.current = true;
    if (activeTab !== 'brokerage') {
      const { contacts, total } = await ContactController.getContactsList(
        activeTab,
        startIndex,
        stopIndex - startIndex + 1,
        sort?.order ? sort : undefined,
        filter,
        true,
        true,
      );
      setData([...(!startIndex ? [] : data), ...contacts]);
      setItemCount(total);
    } else {
      const { users: contacts, count } = await API.GET.orgUsers({
        locations: filter?.location?.join(','),
        sort: SORT_KEY_MAP[sort?.order] && sort?.field,
        order: SORT_KEY_MAP[sort?.order],
        role: filter?.role,
        from: startIndex,
        size: stopIndex - startIndex + 1,
        orgWide: true,
      });
      setData([...contacts]);
      setItemCount(count);
    }
    isLoadingMore.current = false;
  };

  const loadMoreSearchItems = async (startIndex: number, stopIndex: number) => {
    if (isLoadingMoreSearch.current) return;
    isLoadingMoreSearch.current = true;
    let userSearchPromise = Promise.resolve({
      count: searchCounts?.brokerage,
      users: [],
    });
    if (searchText !== prevSearchText || activeTab === 'brokerage') {
      userSearchPromise = API.GET.orgUsers({
        from: startIndex,
        size: stopIndex - startIndex + 1,
        multi: searchText,
        locations: filter?.location?.join(','),
        sort: SORT_KEY_MAP[sort?.order] && sort?.field,
        order: SORT_KEY_MAP[sort?.order],
        role: filter?.role,
        orgWide: true,
      });
    }
    setPrevSearchText(searchText);
    const [
      { count: brokerageCount, users: brokerageUsers },
      {
        active: { contacts, total },
        counts,
      },
    ] = await Promise.all([
      userSearchPromise,
      ContactController.searchContacts(
        searchText,
        activeTab === 'brokerage' ? ListContactType.Client : activeTab,
        startIndex,
        stopIndex - startIndex + 1,
        sort?.order ? sort : undefined,
        filter,
        true,
        true,
      ),
    ]);
    setSearchCounts({ ...counts, brokerage: brokerageCount });
    setSearchData([
      ...(!startIndex ? [] : searchData),
      ...(activeTab === 'brokerage' ? brokerageUsers : contacts),
    ]);
    setSearchItemCount(activeTab === 'brokerage' ? brokerageCount : total);
    isLoadingMoreSearch.current = false;
  };

  useEffect(() => {
    setData([]);
    setSearchData([]);
    setSelectedIds([]);
    setInitialLoading(true);
    (async () => {
      switch (activeTab) {
        case 'brokerage':
          if (searchActive && searchText) {
            setInitialSearchLoading(true);
            await loadMoreSearchItems(
              PAGE_SIZE * (page - 1),
              PAGE_SIZE * page - 1,
            );
            setInitialSearchLoading(false);
          } else {
            await loadMoreItems(PAGE_SIZE * (page - 1), PAGE_SIZE * page - 1);
          }
          break;
        default:
          if (searchActive && searchText) {
            setInitialSearchLoading(true);
            await loadMoreSearchItems(0, 15);
            setInitialSearchLoading(false);
          } else {
            await loadMoreItems(0, 15);
          }
      }
      setInitialLoading(false);
    })();
  }, [activeTab, sort, reloadToggle, searchText, filter, page]);

  const onSelect = useCallback(
    (selectedKeys: string[]) => {
      setSelectedIds(selectedKeys);
    },
    [setSelectedIds],
  );
  if (!auth || (auth.isAdmin && !isUserLicensed(auth))) {
    return null;
  }

  const onFilterSortChange: InfiniteTableProps['onChange'] = (
    _paginate,
    filters,
    sortDetails,
    extra,
  ) => {
    if (extra.action === 'paginate') return;
    // Sorting
    const { field, order } = sortDetails as SorterResult<any>;
    setSort({ field: field as string, order: order as ContactListSortOrder });
    // Filters
    const cleanFilters = Object.entries(filters).reduce(
      (acc, [key, values]) => {
        if (!values) return acc;
        return {
          ...acc,
          [key]: values,
        };
      },
      {},
    );
    setFilter(Object.keys(cleanFilters).length ? cleanFilters : null);
  };

  const onAddContactClick = () => {
    openContactDrawer(ContactDrawerMode.Add, ContactDrawerType.Contact);
  };

  const onSearchChange = (value: string) => {
    if (debouncedSearchId.current) clearTimeout(debouncedSearchId.current);
    if (!value) {
      setSearchActive(false);
      setSearchText(value);
      return;
    }
    debouncedSearchId.current = setTimeout(() => {
      setSearchActive(true);
      setSearchText(value);
      setSearchData([]);
    }, 500);
  };

  const onRowClick = (index: number, user: ContactDrawerUser) => {
    const type =
      activeTab === 'brokerage'
        ? ContactDrawerType.User
        : ContactDrawerType.Contact;

    openContactDrawer(ContactDrawerMode.Profile, type, user.id);
  };

  const onTabChange = (newTab: ListContactType) => {
    history.push(`/contacts/${newTab}`);
    setSort(null);
    setFilter(null);
    setActiveTab(newTab);
  };

  const onDelete = async () => {
    setInitialLoading(true);
    const { archived } = await ContactController.archiveByIds(selectedIds);
    message.success(`Archived ${archived} contact(s)`);
    setSelectedIds([]);
    triggerReload();
  };

  const onRestore = async (contactId: string) => {
    await ContactController.restoreByIds([contactId]);
    message.success('Restored Contact');
    triggerReload();
  };

  return (
    <Page
      title={md ? 'Contacts' : ''}
      type="filled"
      fillScreenHeight
      headerVerticalAlign={xl ? 'center' : 'baseline'}
      bodyCss={pageBodyStyle}
      headerExtra={
        <ContactsHeaderExtras
          onAddContactClick={onAddContactClick}
          onSearchChange={onSearchChange}
          searching={initialSearchLoading}
        />
      }
    >
      <ContactsTab
        sort={sort}
        filter={filter}
        data={searchActive ? searchData : data}
        loading={initialLoading}
        activeTab={activeTab}
        itemCount={searchActive ? searchItemCount : itemCount}
        loadMoreItems={searchActive ? loadMoreSearchItems : loadMoreItems}
        selectedCount={selectedIds.length}
        onFilterSortChange={onFilterSortChange}
        onDelete={onDelete}
        onRestore={onRestore}
        onTabChange={onTabChange}
        onSelect={onSelect}
        onRowClick={onRowClick}
        searchCounts={searchActive ? searchCounts : null}
        page={page}
        setPage={setPage}
      />
    </Page>
  );
};

export default ContactsPage;
